/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  Search, Zap, CheckCircle, AlertCircle, Globe,
  Instagram, MapPin, Hash, ChevronDown, Plus, Loader
} from "lucide-react";
import { useGrowth } from "../../../context/GrowthContext";

const SOURCES = [
  {
    id: "eventbrite",
    label: "Eventbrite",
    icon: Globe,
    description: "Scrape event organizers from Eventbrite listings",
    queryLabel: "Search term",
    queryPlaceholder: "farmers market, music festival, art fair...",
  },
  {
    id: "google",
    label: "Google",
    icon: Search,
    description: "Find organizer websites via Google search",
    queryLabel: "Search term",
    queryPlaceholder: "event organizer vendor market...",
  },
  {
    id: "instagram",
    label: "Instagram",
    icon: Instagram,
    description: "Find organizers via hashtags",
    queryLabel: "Hashtag (no #)",
    queryPlaceholder: "eventorganizer, vendormarket, festivalseller...",
  },
];

function SourceCard({ source, selected, onClick }) {
  const Icon = source.icon;
  return (
    <button
      onClick={onClick}
      className={`flex items-start gap-3 p-4 rounded-xl border text-left transition w-full ${
        selected
          ? "border-gray-900 bg-gray-900 text-white"
          : "border-gray-200 bg-white hover:border-gray-400 text-gray-700"
      }`}
    >
      <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${selected ? "text-white" : "text-gray-500"}`} />
      <div>
        <p className={`text-sm font-semibold ${selected ? "text-white" : "text-gray-900"}`}>
          {source.label}
        </p>
        <p className={`text-xs mt-0.5 ${selected ? "text-gray-300" : "text-gray-400"}`}>
          {source.description}
        </p>
      </div>
    </button>
  );
}

function LeadPreviewRow({ lead }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{lead.name || lead.eventName || "—"}</p>
        <p className="text-xs text-gray-400 truncate">{[lead.eventName, lead.city].filter(Boolean).join(" · ")}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-3">
        {lead.email && <span className="text-xs text-gray-400">email</span>}
        {lead.instagram && <span className="text-xs text-gray-400">ig</span>}
        {lead.website && <Globe className="w-3 h-3 text-gray-300" />}
      </div>
    </div>
  );
}

const DEFAULT_CITIES = [
  "New York, NY",
  "Miami, FL",
  "Atlanta, GA",
  "Louisville, KY",
];

export default function ScraperPanel({ onClose }) {
  const { bulkImport, reload } = useGrowth();
  const [selectedSource, setSelectedSource] = useState("eventbrite");
  const [query, setQuery] = useState("");
  const [city, setCity] = useState("New York, NY");
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState("idle"); // idle | running | preview | importing | done | error
  const [results, setResults] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [importedCount, setImportedCount] = useState(0);

  const source = SOURCES.find((s) => s.id === selectedSource);

  const handleScrape = async () => {
    if (!query.trim()) return;
    setStatus("running");
    setResults([]);
    setErrorMsg("");

    try {
      const call = httpsCallable(getFunctions(), "scrapeLeads");
      const res = await call({ source: selectedSource, query: query.trim(), city: city.trim(), limit });
      setResults(res.data.leads || []);
      setStatus("preview");
    } catch (err) {
      setErrorMsg(err.message || "Scrape failed. Check your Apify token and try again.");
      setStatus("error");
    }
  };

  const handleImport = async () => {
    if (!results.length) return;
    setStatus("importing");
    try {
      await bulkImport(results);
      setImportedCount(results.length);
      setStatus("done");
      reload();
    } catch {
      setErrorMsg("Import failed.");
      setStatus("error");
    }
  };

  const handleReset = () => {
    setStatus("idle");
    setResults([]);
    setErrorMsg("");
    setQuery("");
    setCity("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-gray-800" />
                <h2 className="text-lg font-semibold text-gray-900">Lead Scraper</h2>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">Powered by Apify — pulls real event organizer data</p>
            </div>
            <button onClick={onClose} className="text-xs text-gray-400 hover:text-gray-700 transition">Close</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Done state */}
          {status === "done" && (
            <div className="text-center py-10">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-lg font-semibold text-gray-900">{importedCount} leads imported</p>
              <p className="text-sm text-gray-400 mt-1">Head to Leads to research and generate outreach.</p>
              <button onClick={onClose} className="mt-6 px-5 py-2 bg-gray-900 text-white text-sm rounded-xl hover:bg-gray-800">
                View Leads
              </button>
            </div>
          )}

          {/* Error state */}
          {status === "error" && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-red-500 bg-red-50 rounded-xl p-4">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <p className="text-sm">{errorMsg}</p>
              </div>
              <button onClick={handleReset} className="text-sm text-gray-600 hover:text-gray-900 underline">
                Try again
              </button>
            </div>
          )}

          {/* Setup + Preview */}
          {["idle", "running", "preview"].includes(status) && (
            <>
              {/* Source picker */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Source</p>
                <div className="grid grid-cols-3 gap-3">
                  {SOURCES.map((s) => (
                    <SourceCard
                      key={s.id}
                      source={s}
                      selected={selectedSource === s.id}
                      onClick={() => setSelectedSource(s.id)}
                    />
                  ))}
                </div>
              </div>

              {/* Search params */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    {source?.queryLabel || "Search term"}
                  </label>
                  <div className="relative">
                    {selectedSource === "instagram" ? (
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    ) : (
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    )}
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={source?.queryPlaceholder}
                      className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">City / Region</label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {DEFAULT_CITIES.map((c) => (
                      <button
                        key={c}
                        onClick={() => setCity(c)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition ${
                          city === c
                            ? "bg-gray-900 text-white border-gray-900"
                            : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                        }`}
                      >
                        {c.split(",")[0]}
                      </button>
                    ))}
                  </div>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="or type any city..."
                      className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Max results</label>
                  <select
                    value={limit}
                    onChange={(e) => setLimit(Number(e.target.value))}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none bg-white"
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>{n} leads</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Preview results */}
              {status === "preview" && results.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    {results.length} leads found — preview
                  </p>
                  <div className="border border-gray-100 rounded-xl overflow-hidden max-h-64 overflow-y-auto">
                    {results.map((lead, i) => (
                      <LeadPreviewRow key={i} lead={lead} />
                    ))}
                  </div>
                </div>
              )}

              {status === "preview" && results.length === 0 && (
                <div className="text-center py-6 text-gray-400 text-sm">
                  No results found. Try a different search term or source.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer actions */}
        {["idle", "running", "preview"].includes(status) && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-800">
              Cancel
            </button>
            <div className="flex gap-3">
              {status === "preview" && results.length > 0 && (
                <button
                  onClick={handleScrape}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-700 transition"
                >
                  Re-run
                </button>
              )}
              {status !== "preview" ? (
                <button
                  onClick={handleScrape}
                  disabled={!query.trim() || status === "running"}
                  className="flex items-center gap-2 px-5 py-2 text-sm bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 transition"
                >
                  {status === "running" ? (
                    <><Loader className="w-4 h-4 animate-spin" /> Scraping...</>
                  ) : (
                    <><Zap className="w-4 h-4" /> Scrape {limit} Leads</>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleImport}
                  disabled={!results.length}
                  className="flex items-center gap-2 px-5 py-2 text-sm bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-40 transition"
                >
                  <Plus className="w-4 h-4" />
                  Import {results.length} Leads
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
